import { useMemo, useState } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LayoutOutlined from '@ant-design/icons/LayoutOutlined';
import HighlightOutlined from '@ant-design/icons/HighlightOutlined';
import BorderInnerOutlined from '@ant-design/icons/BorderInnerOutlined';
import BgColorsOutlined from '@ant-design/icons/BgColorsOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import FontColorsOutlined from '@ant-design/icons/FontColorsOutlined';

import MainCard from 'mantis/components/MainCard';
import IconButton from 'mantis/components/@extended/IconButton';
// import AnimateButton from 'mantis/components/@extended/AnimateButton';

import { ThemeMode } from 'src/config';
import useConfig from 'src/hooks/useConfig';
import SimpleBar from 'src/components/ui/SimpleBar';

import ThemeLayout from './ThemeLayout';
import DefaultThemeMode from './ThemeMode';
import ColorScheme from './ColorScheme';
import ThemeFont from './ThemeFont';
import ThemeWidth from './ThemeWidth';
// import ThemeMenuLayout from './ThemeMenuLayout';

const Customization = () => {
	const { mode } = useConfig();

	const themeLayout = useMemo(() => <ThemeLayout />, []);
	const themeMenuLayout = null; // useMemo(() => <ThemeMenuLayout />, []);
	const themeMode = useMemo(() => <DefaultThemeMode />, []);
	const themeColor = useMemo(() => <ColorScheme />, []);
	const themeWidth = useMemo(() => <ThemeWidth />, []);
	const themeFont = useMemo(() => <ThemeFont />, []);

	const [open, setOpen] = useState(false);
	const handleToggle = () => {
		setOpen(!open);
	};

	const iconBackColorOpen =
		mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

	return (
		<>
			<Box sx={{ flexShrink: 0, ml: 0.75 }}>
				<IconButton
					color="secondary"
					variant="light"
					sx={{
						color: 'text.primary',
						bgcolor: open ? iconBackColorOpen : 'transparent',
					}}
					onClick={handleToggle}
					aria-label="settings toggler"
				>
					<SettingOutlined />
				</IconButton>
			</Box>
			<Drawer
				sx={{ zIndex: 2001 }}
				anchor="right"
				onClose={handleToggle}
				open={open}
				PaperProps={{
					sx: {
						width: 340,
					},
				}}
			>
				{open && (
					<MainCard
						title="Theme Customization"
						sx={{
							border: 'none',
							borderRadius: 0,
							height: '100vh',
							'& .MuiCardHeader-root': {
								color: 'background.paper',
								bgcolor: 'primary.main',
								'& .MuiTypography-root': { fontSize: '1rem' },
							},
						}}
						content={false}
						secondary={
							<IconButton
								shape="rounded"
								size="small"
								onClick={handleToggle}
								sx={{ color: 'background.paper' }}
							>
								<CloseCircleOutlined style={{ fontSize: '1.15rem' }} />
							</IconButton>
						}
					>
						<SimpleBar
							sx={{
								height: 'calc(100vh - 70px)',
								'& .simplebar-content': {
									display: 'flex',
									flexDirection: 'column',
								},
							}}
						>
							<Box
								sx={{
									'& .MuiAccordion-root': {
										borderColor: 'divider',
										'& .MuiAccordionSummary-root': {
											bgcolor: 'transparent',
											flexDirection: 'row',
											pl: 1,
										},
										'& .MuiAccordionDetails-root': { border: 'none' },
										'& .Mui-expanded': { color: 'primary.main' },
									},
								}}
							>
								<Accordion defaultExpanded sx={{ borderTop: 'none' }}>
									<AccordionSummary
										aria-controls="panel1d-content"
										id="panel1d-header"
									>
										<Stack direction="row" spacing={1.5} alignItems="center">
											<IconButton
												disableRipple
												color="primary"
												sx={{ bgcolor: 'primary.lighter' }}
												onClick={handleToggle}
												aria-label="settings toggler"
											>
												<LayoutOutlined />
											</IconButton>
											<Stack>
												<Typography variant="subtitle1" color="text.primary">
													Theme Layout
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Choose your layout
												</Typography>
											</Stack>
										</Stack>
									</AccordionSummary>
									<AccordionDetails>{themeLayout}</AccordionDetails>
								</Accordion>
								{themeMenuLayout && (
									<Accordion defaultExpanded>
										<AccordionSummary
											aria-controls="panel4d-content"
											id="panel4d-header"
										>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<IconButton
													disableRipple
													color="primary"
													sx={{ bgcolor: 'primary.lighter' }}
													onClick={handleToggle}
													aria-label="settings toggler"
												>
													<BorderInnerOutlined />
												</IconButton>
												<Stack>
													<Typography variant="subtitle1" color="text.primary">
														Menu Orientation
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Choose Vertical or Horizontal Menu Orientation
													</Typography>
												</Stack>
											</Stack>
										</AccordionSummary>
										<AccordionDetails>{themeMenuLayout}</AccordionDetails>
									</Accordion>
								)}
								<Accordion defaultExpanded>
									<AccordionSummary
										aria-controls="panel2d-content"
										id="panel2d-header"
									>
										<Stack direction="row" spacing={1.25} alignItems="center">
											<IconButton
												disableRipple
												color="primary"
												sx={{ bgcolor: 'primary.lighter' }}
												onClick={handleToggle}
												aria-label="settings toggler"
											>
												<HighlightOutlined />
											</IconButton>
											<Stack>
												<Typography variant="subtitle1" color="text.primary">
													Theme Mode
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Choose light or dark mode
												</Typography>
											</Stack>
										</Stack>
									</AccordionSummary>
									<AccordionDetails>{themeMode}</AccordionDetails>
								</Accordion>
								<Accordion defaultExpanded>
									<AccordionSummary
										aria-controls="panel3d-content"
										id="panel3d-header"
									>
										<Stack direction="row" spacing={1.5} alignItems="center">
											<IconButton
												disableRipple
												color="primary"
												sx={{ bgcolor: 'primary.lighter' }}
												onClick={handleToggle}
												aria-label="settings toggler"
											>
												<BgColorsOutlined />
											</IconButton>
											<Stack>
												<Typography variant="subtitle1" color="text.primary">
													Color Scheme
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Choose your primary theme color
												</Typography>
											</Stack>
										</Stack>
									</AccordionSummary>
									<AccordionDetails>{themeColor}</AccordionDetails>
								</Accordion>
								{themeWidth && (
									<Accordion defaultExpanded>
										<AccordionSummary
											aria-controls="panel4d-content"
											id="panel4d-header"
										>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<IconButton
													disableRipple
													color="primary"
													sx={{ bgcolor: 'primary.lighter' }}
													onClick={handleToggle}
													aria-label="settings toggler"
												>
													<BorderInnerOutlined />
												</IconButton>
												<Stack>
													<Typography variant="subtitle1" color="text.primary">
														Layout Width
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Choose fluid or container layout
													</Typography>
												</Stack>
											</Stack>
										</AccordionSummary>
										<AccordionDetails>{themeWidth}</AccordionDetails>
									</Accordion>
								)}
								<Accordion defaultExpanded sx={{ borderBottom: 'none' }}>
									<AccordionSummary
										aria-controls="panel4d-content"
										id="panel4d-header"
									>
										<Stack direction="row" spacing={1.5} alignItems="center">
											<IconButton
												disableRipple
												color="primary"
												sx={{ bgcolor: 'primary.lighter' }}
												onClick={handleToggle}
												aria-label="settings toggler"
											>
												<FontColorsOutlined />
											</IconButton>
											<Stack>
												<Typography variant="subtitle1" color="text.primary">
													Font Family
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Choose your font family.
												</Typography>
											</Stack>
										</Stack>
									</AccordionSummary>
									<AccordionDetails>{themeFont}</AccordionDetails>
								</Accordion>
							</Box>
						</SimpleBar>
					</MainCard>
				)}
			</Drawer>
		</>
	);
};

export default Customization;
